<script setup lang="ts">
import type { INewsDataClientDTO } from '~/shared/api/client/news/news.dto';
import { resizeCropSlot } from '~/shared/lib/resizeImage';
import { CROP_RATIO_CHRONICLE } from '~/shared/lib/crop-ratio.const';
import { NEWS_ITEM_OBJECT_IMAGE_WIDTH } from './lib/const';

const props = defineProps<INewsDataClientDTO>();

const isRedirect = computed(() =>
  Boolean(props.news.redirect && props.news.redirect.length > 0),
);

const link =
  isRedirect.value && props.news.redirect
    ? props.news.redirect
    : `/${props.news.type}/${props.news.id}`;

const images = computed(() => {
  return [
    props.image_one?.file_url ??
      props.news.gallery?.photos?.[0]?.photo.file_url,
    props.image_two?.file_url ??
      props.news.gallery?.photos?.[1]?.photo.file_url,
    props.image_three?.file_url ??
      props.news.gallery?.photos?.[2]?.photo.file_url,
    props.image_four?.file_url ??
      props.news.gallery?.photos?.[3]?.photo.file_url,
  ];
});

/* У всех четырёх картинок блока одна пропорция — 230x320. */
const imageAttrs = computed(() =>
  images.value.map((url) =>
    resizeCropSlot(url, CROP_RATIO_CHRONICLE.object_images, 0, {
      width: NEWS_ITEM_OBJECT_IMAGE_WIDTH,
    }),
  ),
);

const handleClick = () => {
  if (isRedirect.value) {
    navigateTo(link, {
      external: true,
      open: {
        target: '_blank',
      },
    });
  } else {
    navigateTo(link);
  }
};
</script>

<template>
  <div :class="$style.item" @click="handleClick">
    <div :class="$style.item__header">
      <div :class="$style.item__headerTitle">{{ props.title }}</div>
      <Button
        :class="$style.item__headerButton"
        theme="white"
        size="small"
        :to="link"
      >
        Подробнее
      </Button>
    </div>
    <div :class="$style.item__gallery">
      <div
        v-for="(item, key) in imageAttrs"
        :key="`newsItemGalleryWidePhoto${key}`"
        :class="$style.item__galleryItem"
      >
        <img
          :class="$style.item__galleryImage"
          :src="item.src"
          :srcset="item.srcset || undefined"
          :alt="props.title"
        />
      </div>
    </div>
    <div :class="$style.item__footer">
      <Button
        :class="$style.item__footerButton"
        theme="white"
        size="small"
        :to="link"
      >
        Подробнее
      </Button>
    </div>
  </div>
</template>

<style lang="scss" module>
.item {
  cursor: pointer;
  background: var(--color-black-1);

  &__header {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    padding: 11px var(--space-2-5x);
    justify-content: space-between;
    gap: 200px;

    &Title {
      @include text_h5;
      color: var(--color-white);
    }

    &Button {
      height: 40px;
    }
  }

  &__gallery {
    display: flex;
    flex-flow: row nowrap;
    border-top: 1px solid var(--color-gray-line);
    @include mobileSliderWrapper;

    &Item {
      position: relative;
      display: flex;
      justify-content: center;
      flex-flow: row nowrap;
      border-right: 1px solid var(--color-gray-line);
      width: 100%;
      padding: var(--space-2-5x);

      &:last-child {
        border-right: none;
      }
    }

    &Image {
      width: 230px;
      height: 320px;
      object-fit: cover;
    }
  }

  &__footer {
    display: none;
  }
}

@media (hover: hover) {
  .item__headerTitle,
  .item__galleryItem {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 1379px) {
  .item {
    &__header {
      gap: var(--space-2x);
    }
  }
}

@media (max-width: 767px) {
  .item {
    &__gallery {
      @include mobileSliderWrapper;

      &Item {
        @include mobileSliderItem;
        width: 262px;
        padding: var(--space-2-5x);
      }
    }
  }
}

@media (max-width: 479px) {
  .item {
    &__header {
      padding: var(--space-2x);

      &Button {
        display: none;
      }
    }

    &__gallery {
      border-bottom: 1px solid var(--color-gray-line);
      &Item {
        padding: var(--space-2x);
      }
    }

    &__footer {
      display: block;
      padding: var(--space-2x);

      &Button {
        width: 100%;
        height: 40px;
      }
    }
  }
}
</style>
